import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const SchedulingCX: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "SchedulingCX.sections.executiveSummary" },
    { id: "shifting-expectations", labelKey: "SchedulingCX.sections.shiftingExpectations" },
    { id: "cost-inefficiency", labelKey: "SchedulingCX.sections.costInefficiency" },
    { id: "self-service-access", labelKey: "SchedulingCX.sections.selfServiceAccess" },
    { id: "smart-scheduling", labelKey: "SchedulingCX.sections.smartScheduling" },
    { id: "digital-front-door", labelKey: "SchedulingCX.sections.digitalFrontDoor" },
    { id: "conclusion", labelKey: "SchedulingCX.sections.conclusion" },
    { id: "references", labelKey: "SchedulingCX.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | SchedulingCX
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Healthcare scheduling has emerged as one of the most strategic enablers of patient satisfaction, access to care, and provider efficiency. Patients now expect a seamless, consumer-grade digital experience, while health systems must also balance complex capacity management.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="10 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "Digital Front Doors in Healthcare", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "shifting-expectations" && "🤖 1. Shifting Expectations"}
                {s.id === "cost-inefficiency" && "📊 2. The Cost of Inefficient Scheduling"}
                {s.id === "self-service-access" && "💡 3. Expanding Self-Service Access"}
                {s.id === "smart-scheduling" && "⚠️ 4. Smart Scheduling & AI"}
                {s.id === "digital-front-door" && "🛠️ 5. The Digital Front Door of the Future"}
                {s.id === "conclusion" && "✅ 6. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Healthcare scheduling has emerged as one of the most strategic enablers of patient satisfaction, access to care, and provider efficiency. Patients now expect a seamless, consumer-grade digital experience, while health systems must also balance complex capacity management. Yet many still rely on manual call centers and outdated systems, contributing to billions in lost revenue and avoidable inequities.
                    </p>
                    <br />
                    <p>
                      SchedulingCX provides a comprehensive solution that enables patients, referring providers, and organizations to book directly into capacity-managed systems. With embedded AI optimization, predictive analytics, and self-service portals, SchedulingCX reduces no-shows, improves utilization, and delivers a true digital front door.
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> 77% of patients say that the ability to self-schedule appointments influences their choice of provider.</p>
                    </div>
                  </>
                )}
                {s.id === "shifting-expectations" && (
                  <>
                    <p>
                      Patients no longer compare healthcare scheduling to other hospitals—they compare it to their daily experiences with Uber, Amazon, or OpenTable. Referring providers also expect real-time access to booking so that patients can receive timely specialty care.
                    </p>
                    <br />
                    <p>
                      According to Accenture, 77% of patients say that the ability to self-schedule appointments influences their choice of provider [1]. This shift represents a fundamental change: scheduling is now a competitive differentiator in healthcare.
                    </p>
                  </>
                )}
                {s.id === "cost-inefficiency" && (
                  <>
                    <p>
                      Inefficiencies in scheduling are costly for both patients and providers:
                    </p>
                    <br />
                    <ul>
                      <li>- No-show rates in U.S. outpatient care average between 18–30% [2].</li>
                      <li>- Each missed appointment costs a physician practice approximately $200, adding up to an estimated $150 billion annually [3].</li>
                      <li>- Call center scheduling results in long hold times and frustrated patients.</li>
                    </ul>
                    <br />
                    <p>
                      The impact extends beyond finances: delays caused by inefficient scheduling can worsen health outcomes, increase emergency department visits, and reduce patient trust.
                    </p>
                  </>
                )}
                {s.id === "self-service-access" && (
                  <>
                    <p>
                      Self-service scheduling extends beyond patients. Community providers and referring organizations must also have access to book into your system. This reduces leakage, ensures equitable access, and simplifies workflows across networks.
                    </p>
                    <br />
                    <p>
                      SchedulingCX makes this possible with portals designed for patients, providers, and external referrers, enabling truly collaborative scheduling.
                    </p>
                  </>
                )}
                {s.id === "smart-scheduling" && (
                  <>
                    <p>
                      AI-powered scheduling allows systems to:
                    </p>
                    <br />
                    <ul>
                      <li>- Optimize appointment slots to minimize unused capacity.</li>
                      <li>- Forecast demand surges and adjust staffing.</li>
                      <li>- Allocate resources based on predictive models.</li>
                    </ul>
                    <br />
                    <p>
                      This transforms scheduling from a reactive process into a strategic, data-driven capability.
                    </p>
                  </>
                )}
                {s.id === "digital-front-door" && (
                  <>
                    <p>
                      The future of healthcare access requires omnichannel scheduling—integrated across web portals, mobile apps, call centers, and EHR systems. SchedulingCX provides this functionality out of the box, making it possible to deliver a seamless digital front door for all stakeholders..
                    </p>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      Scheduling is no longer a back-office task; it is the cornerstone of patient experience and provider performance. By adopting SchedulingCX, health systems can unlock significant operational and financial benefits while delivering the seamless access patients now expect.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. Accenture Digital Health Consumer Survey, 2019.</li>
                      <li>2. Journal of General Internal Medicine, 2019. Average no-show rates in outpatient care.</li>
                      <li>3. Forbes/MGMA Report, 2020. Missed appointments cost $150 billion annually.</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Outpatient No-show Rates (%)</li>
                        <li>• Estimated Cost of Missed Appointments (USD B)</li>
                        <li>• Adoption of Self-Scheduling in Healthcare (%)</li>
                      </ul>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Outpatient No-show Rates</h5>
                        <img 
                          src="/Image/WhitePapers/SchedulingCX_WhitePaper_New_Format/image1.png" 
                          alt="Outpatient No-show Rates Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Estimated Cost of Missed Appointments</h5>
                        <img 
                          src="/Image/WhitePapers/SchedulingCX_WhitePaper_New_Format/image2.png" 
                          alt="Estimated Cost of Missed Appointments Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Adoption of Self-Scheduling in Healthcare</h5>
                        <img 
                          src="/Image/WhitePapers/SchedulingCX_WhitePaper_New_Format/image3.png" 
                          alt="Adoption of Self-Scheduling in Healthcare Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how digital scheduling transforms healthcare access through self-service portals, AI optimization, and omnichannel integration.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>77% of patients prefer self-scheduling</li>
                <li>$150B annual cost from missed appointments</li>
                <li>AI-powered capacity optimization</li>
                <li>Omnichannel digital front door</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "shifting-expectations" && "Shifting Expectations"}
                      {s.id === "cost-inefficiency" && "The Cost of Inefficient Scheduling"}
                      {s.id === "self-service-access" && "Expanding Self-Service Access"}
                      {s.id === "smart-scheduling" && "Smart Scheduling & AI"}
                      {s.id === "digital-front-door" && "The Digital Front Door of the Future"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "The Future of Referral Management in Healthcare", description: "From fax to AI-powered workflows that eliminate administrative waste and improve access.", route: "/whitepapers/referral-cx" },
              { title: "AI in Healthcare Operations", description: "Practical applications for smarter collaboration and better care delivery.", route: "/whitepapers/ai-in-healthcare-operations" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs.", route: "/whitepapers/integrated-care" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever"
        pdfPath="/PDF/WhitePapers/SchedulingCX_WhitePaper.pdf"
      />
    </div>
  )
}

export default SchedulingCX
